import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { getWOActivity } from "@/lib/api";
import { PillRail, Pill } from "@/components/common/Segmented";

const KINDS = [
  { key: "", label: "All" }, { key: "status", label: "Status" }, { key: "people", label: "People" }, { key: "edit", label: "Edits" },
  { key: "money", label: "Money" }, { key: "co", label: "COs" }, { key: "email", label: "Emails" }, { key: "foundation", label: "Foundation" }, { key: "note", label: "Notes" },
];
const DOT = { status: "bg-blue-500", people: "bg-indigo-400", edit: "bg-slate-400", money: "bg-emerald-500", co: "bg-amber-500", email: "bg-sky-400", foundation: "bg-violet-500", note: "bg-slate-300" };

const when = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString([], { month: "numeric", day: "numeric", year: "2-digit", hour: "numeric", minute: "2-digit" });
};

// Activity tab — the in-app event trail for a WO: when · who · what, one line each, newest first.
export function WOActivityTrail({ woId }) {
  const [rows, setRows] = useState(null);
  const [kind, setKind] = useState("");
  const load = () => getWOActivity(woId).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { if (woId) load(); }, [woId]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = (rows || []).filter((r) => !kind || r.kind === kind);
  return (
    <div className="space-y-2" data-testid="wo-activity-trail">
      <div className="flex flex-wrap items-center gap-2">
        <PillRail testId="wo-activity-kinds">
          {KINDS.map((k) => <Pill key={k.key} on={kind === k.key} onClick={() => setKind(k.key)} testId={`wo-activity-kind-${k.key || "all"}`}>{k.label}</Pill>)}
        </PillRail>
        <button type="button" onClick={load} aria-label="Refresh" title="Refresh"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50" data-testid="wo-activity-refresh">
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
        <span className="ml-auto text-xs text-slate-500 num" data-testid="wo-activity-count">{shown.length} event{shown.length === 1 ? "" : "s"}</span>
      </div>
      <div className="data-table-container divide-y divide-slate-100">
        {rows === null ? (
          <div className="px-3 py-6 text-center text-xs text-slate-400">Loading activity…</div>
        ) : shown.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-slate-400" data-testid="wo-activity-empty">No activity yet.</div>
        ) : shown.map((r, i) => (
          <div key={`${r.at}-${i}`} className="grid grid-cols-[130px_150px_1fr] items-center gap-3 px-3 py-1.5 text-xs" data-testid={`wo-activity-row-${i}`}>
            <span className="text-slate-500 num whitespace-nowrap">{when(r.at)}</span>
            <span className="truncate text-slate-700" title={r.who}>{r.who}</span>
            <span className="flex min-w-0 items-center gap-2 text-slate-800">
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT[r.kind] || "bg-slate-300"}`} />
              <span className="truncate" title={r.what}>{r.what}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
